'use client';
import { StyledHeroInteractiveBanner } from '@/components/common/ygen/sections/hero-banner/herobanner.style';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import useAutoplay from '@/components/hooks/useAutoplay';
import Slider, { Slide } from '@/components/common/swiper';
import { SlideOne as SlideItem } from '@/components/common/swiper/slide-one';
import { NavigationSlider as NavigationItem } from '@/components/common/swiper/navigation-slider';
import ThemeRow from '@/components/features/theme/row';
import { Col } from 'react-bootstrap';
import { gsap } from 'gsap';
import { usePathname } from 'next/navigation';
import useContainerOffset from '@/components/hooks/useContainerOffset';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
// import useAnimatedText from '@/components/animations/useAnimatedText'; //comment for optimize
import { CURSOR_TEXT, DispatchCursor } from 'haspr-cursor';


const SliderMain = ({ containerGap, data, settings, className }) => {
	// Refs for main home-slider and navigation home-slider
	const mainSliderRef = useRef(null);
	const navigationSliderRef = useRef(null);
	const [current, setCurrent] = useState('1');
	const pathName = usePathname();
	const [activeSlideIndex, setActiveSlideIndex] = useState(0); // State for active slide index
	let offset = useContainerOffset('.container');
	const dispatch = DispatchCursor();

	useAutoplay(mainSliderRef); // Hook for autoplay

	// Function to handle progress of slides
	const handleProgress = (swiper, event) => {
		const interleaveOffset = 0.5;
		const { slides } = swiper;

		slides.forEach((slide) => {
			const slideProgress = slide.progress;
			const innerOffset = swiper.width * interleaveOffset;
			const innerTranslate = slideProgress * innerOffset;
			const globalImage = slide.querySelector('.global-image');

			if (globalImage) {
				globalImage.style.transform = `translate3d(${innerTranslate}px, 0, 0)`;
			}
		});
	};

	// Function to set transition of slides
	const handleSetTransition = (swiper, speed) => {
		swiper.slides.forEach((slide) => {
			slide.style.transition = `${speed}ms`;
			const image = slide.querySelector('.global-image');
			if (image) {
				image.style.transition = `${speed}ms`;
			}
		});
	};

	// Function to handle slide change event
	const handleSlideChange = (event) => {
		const newActiveSlideIndex = event.realIndex;
		setActiveSlideIndex(newActiveSlideIndex);
		if (navigationSliderRef?.current) {
			navigationSliderRef?.current?.swiper?.slideTo(newActiveSlideIndex);
		}
		setTimeout(() => {
			if (document.querySelector('.home-banner .swiper-pagination-current')) {
				setCurrent(document.querySelector('.home-banner .swiper-pagination-current').innerHTML);
			}
		}, 200);
	};

	// Function to handle pagination button click
	const handleButtonClickPagination = (index) => {
		if (mainSliderRef?.current) {
			setActiveSlideIndex(index);
			mainSliderRef?.current.swiper.slideToLoop(index);
		}
	};

	// Function to initialize swiper
	const handleSwiperInit = (swiper) => {
		return swiper;
	};



	useEffect(() => {
		const ctx = gsap.context(() => {
			gsap.set('.home-banner', { opacity: 0 });
			gsap.to('.home-banner', {
				opacity: 1,
				ease: 'easeInOut',
				duration: 0.5,
			});
		});

		return () => ctx.revert(); // This will clean up all GSAP animations
	}, [pathName]);

	// useAnimatedText([]) //comment for optimize

	const renderSliderItem = useMemo(() => {
		return data?.posts?.list?.map((e, index) => {
			let isVideo = e?.images?.find(f => f?.is_video === 'on');
			let isBanner = e?.images?.find(f => f?.banner === 'on');

			if (isVideo) {

				return (
					<Slide key={index}>
						<SlideItem
							topTitle={e?.data?.subtitle}
							title={e?.data?.subtitle}
							video
							currentIndex={index}
							bannerLarge={
								isVideo

									? isVideo?.full_path
									: '/videos/static/sample_video_2.mp4'
							}
							bannerSmall={
								isVideo

									? isVideo?.full_path
									: '/videos/static/sample_video_2.mp4'
							}
							buttonLabel={''}
							buttonLink={''}
							buttonTarget={'self'}
							description={e?.data?.short_desc ? e?.data?.short_desc : ''}
						/>
					</Slide>
				);
			} else {

				return (
					<Slide key={index}>
						<SlideItem
							currentIndex={index}
							classname={'anim-char'}
							topTitle={e?.data?.subtitle}
							title={e?.data?.subtitle}
							bannerLarge={isBanner?.full_path}
							bannerSmall={isBanner?.full_path}
							buttonLabel={''}
							buttonLink={''}
							buttonTarget={'self'}
							description={e?.data?.short_desc ? e?.data?.short_desc : ''}
						/>
					</Slide>
				);
			}

		});
	}, [data?.posts]);


	const renderSliderNavigationItem = useMemo(() => {
		return data?.posts?.list?.map((e, index) => {
			return (
				<SwiperSlide key={index}>
					<NavigationItem
						activeIndex={activeSlideIndex}
						index={index}
						onClick={handleButtonClickPagination}
					/>
				</SwiperSlide>
			);
		});
	});

	// demo length
	return (
		<StyledHeroInteractiveBanner id="hero-banner" data-scroll-section
								 offset={offset}
								 containerGap={containerGap}
								 className={'home-banner'}
		>
			<div className="main-banner-wrapper-stick">
				{data?.posts?.list && data?.posts?.list?.length > 0 && (
					<Slider
						ref={mainSliderRef}
						settings={settings}
						className={className}
						onSwiperInit={handleSwiperInit}
						spaceBetween={0}
						loop={true}
						speed={1000}
						onSlideChange={handleSlideChange}
						onProgress={handleProgress}
						onSetTransition={handleSetTransition}
						grabCursor={false}
						watchSlidesProgress={true}
						mousewheelControl={true}
						keyboardControl={true}
						pagination={{
							// el: '.swiper-pagination',
							type: 'fraction',
						}}
						nextEl={'.swiper-button-next'}
						prevEl={'.swiper-button-prev'}

					>
						{data?.posts?.list &&
							data?.posts?.list?.length > 0 &&
							renderSliderItem

						}

						{
							data?.posts?.list && data?.posts?.list?.length > 0 &&
							<>
								<div onMouseEnter={() => CURSOR_TEXT(dispatch, <img height={40} width={40} src="/images/static/slider-left.svg" alt="" />, 'theme')} onMouseLeave={() => CURSOR_TEXT(dispatch, 'END')} className="swiper-button-prev" />
								<div onMouseEnter={() => CURSOR_TEXT(dispatch, <img height={40} width={40} src="/images/static/slider-right.svg" alt="" />, 'theme')} onMouseLeave={() => CURSOR_TEXT(dispatch, 'END')} className="swiper-button-next" />
							</>

						}

					</Slider>
				)}


				{

					data?.posts?.list && data?.posts?.list?.length > 0 &&
					<div className={'custom-navigation-home-slider-wrapper'}>
						<ThemeRow>
							<Col md={{ span: 6, offset: 6 }}>
								<div className="d-flex bottom-navigation-wrapper-home">


									{data?.posts?.list && data?.posts?.list?.length > 0 && (
										<Swiper
											ref={navigationSliderRef}
											slidesPerView={4}
											spaceBetween={0}
											speed={1000}
											loop={false}
											autoplay={false}
											grabCursor={false}
											breakpoints={{
												320: {
													slidesPerView: 1,
													spaceBetween: 30,

												},
												768: {
													slidesPerView: 1,
													spaceBetween: 30,

												},
												1024: {
													slidesPerView: 4,
													spaceBetween: 30,
												},
											}}
											modules={[Autoplay, Pagination, Navigation]}
											className="mySwiperNext"
										>
											{data?.posts?.list &&
												data?.posts?.list?.length > 0 &&
												renderSliderNavigationItem

											}
										</Swiper>
									)}

									<div className="current_total">
										0{current}
									</div>
								</div>
							</Col>
						</ThemeRow>
					</div>

				}
			</div>


		</StyledHeroInteractiveBanner>
	);
};



export default React.memo(SliderMain);
